import React, { useState, useMemo } from 'react';
import {
  Loader2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Bug,
  CheckCircle,
  Clock,
  Users,
  GitBranch,
  Zap,
  ArrowRight,
  RefreshCw,
  Shield,
  BarChart3,
  Target,
  Activity,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useExecutiveDashboard } from '../../hooks/useJira';
import TechnologyFilters from '../../components/TechnologyFilters';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from 'recharts';
import { formatDate } from '../../utils/formatters';

// ─── Helper: format date range for display ───
function formatDateRange(startDate, endDate) {
  if (!startDate && !endDate) return 'All Time';
  if (startDate && endDate) return `${formatDate(startDate)} – ${formatDate(endDate)}`;
  if (startDate) return `From ${formatDate(startDate)}`;
  return `Until ${formatDate(endDate)}`;
}

// ─── Helper: health status ───
function getSprintHealth(team) {
  if (team.error) return { status: 'error', color: 'gray' };
  const total = team.avgVelocity + team.avgSpillover;
  if (total === 0) return { status: 'No Data', color: 'gray' };
  const rate = (team.avgVelocity / total) * 100;
  if (rate >= 80) return { status: 'Healthy', color: 'green' };
  if (rate >= 60) return { status: 'Needs Attention', color: 'amber' };
  return { status: 'At Risk', color: 'red' };
}

function getKanbanHealth(team) {
  if (team.error) return { status: 'error', color: 'gray' };
  if (team.openBugCount > 10 || team.avgTurnaroundDays > 10) return { status: 'At Risk', color: 'red' };
  if (team.openBugCount > 5 || team.avgTurnaroundDays > 5) return { status: 'Needs Attention', color: 'amber' };
  return { status: 'Healthy', color: 'green' };
}

const HEALTH_DOT = {
  green: 'bg-green-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  gray: 'bg-gray-400',
};

const HEALTH_BADGE = {
  green: 'bg-green-100 text-green-700',
  amber: 'bg-amber-100 text-amber-700',
  red: 'bg-red-100 text-red-700',
  gray: 'bg-gray-100 text-gray-500',
};

// ─── Custom tooltip for charts ───
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
      <p className="font-medium text-gray-900 text-sm mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-sm" style={{ color: entry.color }}>
          {entry.name}: <span className="font-semibold">{entry.value}</span>
        </p>
      ))}
    </div>
  );
}

// ─── Custom bar label ───
function renderBarLabel({ x, y, width, value }) {
  if (!value) return null;
  return (
    <text x={x + width + 4} y={y + 14} fill="#374151" fontSize={12} fontWeight={600}>
      {value}
    </text>
  );
}

// ─── Stat Card ───
function StatCard({ title, value, subtitle, icon: Icon, gradient }) {
  return (
    <div className={`rounded-xl p-5 shadow-sm border border-white/20 ${gradient}`}>
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-white/25 rounded-lg backdrop-blur-sm">
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-sm text-white/80 font-medium">{title}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
          {subtitle && <p className="text-xs text-white/60 mt-0.5">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}

// ─── Team Card ───
function TeamCard({ team, type, sectionKey, onNavigate }) {
  if (team.error) {
    return (
      <div className="bg-white rounded-xl border border-red-200 p-5 shadow-sm">
        <h4 className="font-semibold text-gray-900 mb-1">{team.team}</h4>
        <p className="text-sm text-red-500">{team.error}</p>
      </div>
    );
  }

  const health = type === 'sprint' ? getSprintHealth(team) : getKanbanHealth(team);

  return (
    <button
      onClick={() => onNavigate(sectionKey)}
      className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md hover:border-amber-300 transition-all block w-full text-left group"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${HEALTH_DOT[health.color]}`} />
          <h4 className="font-semibold text-gray-900">{team.team}</h4>
        </div>
        <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-amber-500 transition-colors" />
      </div>

      {type === 'sprint' ? (
        <div className="space-y-2.5">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Avg Velocity</span>
            <span className="font-semibold text-amber-600">{team.avgVelocity} pts</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Avg Spillover</span>
            <span className={`font-semibold ${team.avgSpillover > 5 ? 'text-red-600' : 'text-green-600'}`}>
              {team.avgSpillover} pts
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Completion Rate</span>
            <span className="font-semibold text-gray-900">
              {team.avgVelocity + team.avgSpillover > 0
                ? Math.round((team.avgVelocity / (team.avgVelocity + team.avgSpillover)) * 100)
                : 0}%
            </span>
          </div>
          {team.activeSprint && (
            <div className="pt-2.5 border-t border-gray-100">
              <div className="flex justify-between items-center mb-1.5">
                <span className="text-xs font-medium text-gray-500">Active Sprint</span>
                <span className="text-xs font-bold text-amber-600">{team.activeSprint.progress}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div
                  className="bg-amber-500 h-2 rounded-full transition-all"
                  style={{ width: `${team.activeSprint.progress}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-1 truncate">{team.activeSprint.name}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2.5">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Open Bugs</span>
            <span className={`font-semibold ${team.openBugCount > 10 ? 'text-red-600' : 'text-green-600'}`}>
              {team.openBugCount}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Total Issues</span>
            <span className="font-semibold text-gray-700">{team.totalIssues}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Avg Turnaround</span>
            <span className={`font-semibold ${team.avgTurnaroundDays > 7 ? 'text-orange-600' : 'text-green-600'}`}>
              {team.avgTurnaroundDays} days
            </span>
          </div>
          {team.last30Days && (
            <div className="pt-2.5 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Last 30 Days</span>
                <span className="text-xs">
                  <span className="text-green-600 font-medium">+{team.last30Days.created}</span>
                  {' / '}
                  <span className="text-amber-600 font-medium">-{team.last30Days.resolved}</span>
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </button>
  );
}

// ─── Helper: get current month date range ───
function getCurrentMonthDateRange() {
  const today = new Date();
  const startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const endDate = today.toISOString().split('T')[0];
  return { startDate, endDate };
}

// ─── MAIN COMPONENT ───
export default function TechnologyDashboard({ onNavigate }) {
  const [filters, setFilters] = useState(() => getCurrentMonthDateRange());
  const [highlightsOpen, setHighlightsOpen] = useState(false);
  const { data, isLoading, isError, error, refetch, isFetching } = useExecutiveDashboard(filters);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Derived data
  const dashboard = data?.data;
  const { summary, sprintTeams, kanbanTeams } = dashboard || {};

  // Helper: summarize issue titles into plain-English milestone descriptions
  const summarizeWork = (issues) => {
    if (!issues || issues.length === 0) return '';
    const summaries = issues.map((i) => (i.summary || '').toLowerCase());

    // Detect themes from keywords and group into business-friendly descriptions
    const themes = [];
    const matched = new Set();

    const patterns = [
      { keywords: ['api', 'endpoint', 'integration', 'webhook', 'rest'], label: 'API and integration improvements' },
      { keywords: ['auth', 'login', 'sso', 'password', 'permission', 'role', 'access'], label: 'authentication and access enhancements' },
      { keywords: ['report', 'dashboard', 'analytics', 'chart', 'metric', 'kpi'], label: 'reporting and analytics updates' },
      { keywords: ['ui', 'ux', 'design', 'layout', 'style', 'button', 'modal', 'page', 'screen', 'form', 'display'], label: 'user interface improvements' },
      { keywords: ['performance', 'speed', 'optimize', 'cache', 'latency', 'slow'], label: 'performance optimizations' },
      { keywords: ['test', 'qa', 'automation', 'unit test', 'e2e'], label: 'quality assurance and testing' },
      { keywords: ['deploy', 'ci', 'cd', 'pipeline', 'infra', 'devops', 'aws', 'cloud'], label: 'infrastructure and deployment updates' },
      { keywords: ['security', 'vulnerability', 'encrypt', 'compliance'], label: 'security enhancements' },
      { keywords: ['email', 'notification', 'sms', 'alert', 'message'], label: 'notification and communication features' },
      { keywords: ['card', 'payment', 'transaction', 'billing', 'fis'], label: 'card and payment processing work' },
      { keywords: ['onboard', 'enrollment', 'registration', 'signup'], label: 'onboarding and enrollment improvements' },
      { keywords: ['data', 'migration', 'import', 'export', 'etl', 'batch'], label: 'data processing and migration work' },
      { keywords: ['bug', 'fix', 'issue', 'error', 'defect', 'crash', 'broken'], label: 'bug fixes and stability improvements' },
      { keywords: ['document', 'doc', 'readme', 'guide'], label: 'documentation updates' },
      { keywords: ['salesforce', 'service cloud', 'case', 'crm'], label: 'Salesforce and CRM enhancements' },
      { keywords: ['marketing', 'campaign', 'content'], label: 'marketing platform updates' },
      { keywords: ['member', 'consumer', 'user', 'client', 'tenant'], label: 'member and client experience improvements' },
      { keywords: ['config', 'setting', 'setup', 'environment'], label: 'configuration and setup work' },
    ];

    summaries.forEach((s, idx) => {
      for (const p of patterns) {
        if (p.keywords.some((kw) => s.includes(kw))) {
          if (!themes.find((t) => t.label === p.label)) {
            themes.push({ label: p.label, count: 0 });
          }
          themes.find((t) => t.label === p.label).count++;
          matched.add(idx);
          break;
        }
      }
    });

    // Count unmatched items as general work
    const unmatched = summaries.length - matched.size;
    if (themes.length === 0) {
      return `${issues.length} item${issues.length > 1 ? 's' : ''} delivered`;
    }

    // Sort themes by count descending, take top 3
    themes.sort((a, b) => b.count - a.count);
    const topThemes = themes.slice(0, 3).map((t) => t.label);
    const result = topThemes.join(', ');
    const remaining = issues.length - themes.slice(0, 3).reduce((s, t) => s + t.count, 0);
    if (remaining > 0) {
      return `${result}, and ${remaining} more item${remaining > 1 ? 's' : ''}`;
    }
    return result;
  };

  // Returns highlights grouped by board/team for easy talking points
  const highlightGroups = useMemo(() => {
    if (!sprintTeams || !kanbanTeams) return [];
    const groups = [];
    const validSprints = sprintTeams.filter((t) => !t.error);
    const validKanban = kanbanTeams.filter((t) => !t.error);

    // Sprint team groups
    validSprints.forEach((team) => {
      const completed = team.completedIssues || [];
      const inProgress = team.inProgressIssues || [];
      if (completed.length === 0 && inProgress.length === 0) return;

      const bullets = [];
      const sprintName = team.activeSprint?.name || '';

      // Completed work
      if (completed.length > 0) {
        const totalPoints = completed.reduce((s, i) => s + (i.points || 0), 0);
        const summary = summarizeWork(completed);
        const pointsLabel = totalPoints > 0 ? ` (${totalPoints} pts)` : '';
        bullets.push({
          text: `Delivered ${completed.length} item${completed.length > 1 ? 's' : ''}${pointsLabel} — ${summary}.`,
          color: 'green',
        });
      }

      // In-progress work
      if (inProgress.length > 0) {
        const totalPoints = inProgress.reduce((s, i) => s + (i.points || 0), 0);
        const summary = summarizeWork(inProgress);
        const pointsLabel = totalPoints > 0 ? ` (${totalPoints} pts)` : '';
        bullets.push({
          text: `Currently working on ${inProgress.length} item${inProgress.length > 1 ? 's' : ''}${pointsLabel} — ${summary}.`,
          color: 'amber',
        });
      }

      // Sprint progress
      if (team.activeSprint) {
        const pct = team.activeSprint.progress || 0;
        bullets.push({
          text: `Sprint is ${pct}% complete (${team.activeSprint.completedPoints} of ${team.activeSprint.totalPoints} pts).`,
          color: pct >= 60 ? 'green' : pct >= 40 ? 'amber' : 'red',
        });
      }

      groups.push({
        team: team.team,
        type: 'sprint',
        subtitle: sprintName,
        bullets,
      });
    });

    // Kanban team groups
    validKanban.forEach((team) => {
      const bullets = [];

      // Resolved vs created
      if (team.last30Days) {
        const { created, resolved } = team.last30Days;
        if (resolved > 0 || created > 0) {
          if (resolved >= created) {
            bullets.push({
              text: `Resolved ${resolved} items vs ${created} new in the last 30 days — keeping pace.`,
              color: 'green',
            });
          } else {
            bullets.push({
              text: `${created} new items vs ${resolved} resolved in the last 30 days — backlog growing by ${created - resolved}.`,
              color: 'amber',
            });
          }
        }
      }

      // Open bugs
      if (team.openBugCount > 0) {
        bullets.push({
          text: `${team.openBugCount} open issue${team.openBugCount > 1 ? 's' : ''} being tracked.`,
          color: team.openBugCount > 10 ? 'red' : team.openBugCount > 5 ? 'amber' : 'green',
        });
      } else {
        bullets.push({
          text: 'No open issues — all clear.',
          color: 'green',
        });
      }

      // Turnaround
      if (team.avgTurnaroundDays > 0) {
        bullets.push({
          text: `Average resolution time: ${team.avgTurnaroundDays} days.`,
          color: team.avgTurnaroundDays <= 5 ? 'green' : team.avgTurnaroundDays <= 10 ? 'amber' : 'red',
        });
      }

      groups.push({
        team: team.team,
        type: 'kanban',
        subtitle: 'Kanban board',
        bullets,
      });
    });

    return groups;
  }, [sprintTeams, kanbanTeams]);

  const velocityData = useMemo(
    () =>
      sprintTeams
        ?.filter((t) => !t.error)
        .map((t) => ({
          team: t.team,
          velocity: t.avgVelocity,
          spillover: t.avgSpillover,
        })) || [],
    [sprintTeams]
  );

  const completionData = useMemo(
    () =>
      sprintTeams
        ?.filter((t) => !t.error && t.avgVelocity + t.avgSpillover > 0)
        .map((t) => {
          const rate = Math.round((t.avgVelocity / (t.avgVelocity + t.avgSpillover)) * 100);
          return { name: t.team, value: rate, fill: rate >= 80 ? '#10b981' : rate >= 60 ? '#f59e0b' : '#ef4444' };
        }) || [],
    [sprintTeams]
  );

  const throughputData = useMemo(
    () =>
      kanbanTeams
        ?.filter((t) => !t.error && t.last30Days)
        .map((t) => ({
          team: t.team,
          created: t.last30Days.created,
          resolved: t.last30Days.resolved,
        })) || [],
    [kanbanTeams]
  );

  const bugData = useMemo(
    () =>
      kanbanTeams
        ?.filter((t) => !t.error)
        .map((t) => ({
          team: t.team,
          bugs: t.openBugCount,
        })) || [],
    [kanbanTeams]
  );

  const overallCompletion = useMemo(() => {
    if (!sprintTeams) return 0;
    const valid = sprintTeams.filter((t) => !t.error);
    const totalVel = valid.reduce((s, t) => s + t.avgVelocity, 0);
    const totalSpill = valid.reduce((s, t) => s + t.avgSpillover, 0);
    return totalVel + totalSpill > 0 ? Math.round((totalVel / (totalVel + totalSpill)) * 100) : 0;
  }, [sprintTeams]);

  // ─── Loading / Error states ───
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-amber-500 animate-spin" />
          <p className="text-gray-500 text-lg">Loading Jira data from all teams...</p>
          <p className="text-gray-400 text-sm">This may take a moment</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4 text-red-500">
          <AlertCircle className="w-12 h-12" />
          <p className="text-lg font-medium">Failed to load Jira data</p>
          <p className="text-sm text-gray-500">{error?.message}</p>
          <button
            onClick={() => refetch()}
            className="mt-2 px-4 py-2 bg-amber-400 text-gray-900 rounded-lg hover:bg-amber-500 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Health scorecard data
  const scorecardRows = [
    ...(sprintTeams || []).map((t) => {
      const health = getSprintHealth(t);
      const rate =
        !t.error && t.avgVelocity + t.avgSpillover > 0
          ? Math.round((t.avgVelocity / (t.avgVelocity + t.avgSpillover)) * 100) + '%'
          : '—';
      return {
        team: t.team,
        teamKey: t.teamKey,
        type: 'Sprint',
        metric: t.error ? '—' : `${t.avgVelocity} pts velocity`,
        secondary: t.error ? '' : `${rate} completion`,
        health,
        sectionKey: `technology-sprint-${t.teamKey}`,
      };
    }),
    ...(kanbanTeams || []).map((t) => {
      const health = getKanbanHealth(t);
      return {
        team: t.team,
        teamKey: t.teamKey,
        type: 'Kanban',
        metric: t.error ? '—' : `${t.openBugCount} open bugs`,
        secondary: t.error ? '' : `${t.avgTurnaroundDays}d turnaround`,
        health,
        sectionKey: `technology-kanban-${t.teamKey}`,
      };
    }),
  ];

  return (
    <div className="space-y-6">
      {/* ─── Header ─── */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
            <GitBranch className="w-6 h-6 sm:w-7 sm:h-7 text-amber-500" />
            Technology Executive Dashboard
          </h2>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">
            Overview of all technology teams &middot;{' '}
            <span className="font-medium text-gray-700">
              {formatDateRange(filters.startDate, filters.endDate)}
            </span>
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 w-full sm:w-auto"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* ─── Filters ─── */}
      <TechnologyFilters
        onFilterChange={handleFilterChange}
        showSprintFilter={false}
        initialFilters={filters}
      />

      {/* ─── Key Highlights by Board (collapsible) ─── */}
      {highlightGroups.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
          <button
            onClick={() => setHighlightsOpen(!highlightsOpen)}
            className="w-full flex items-center justify-between px-5 py-3.5 text-left"
          >
            <h3 className="text-sm font-semibold text-amber-800 uppercase tracking-wider flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Key Highlights
              <span className="text-xs font-normal normal-case text-amber-600">({highlightGroups.length} teams)</span>
            </h3>
            {highlightsOpen ? (
              <ChevronDown className="w-4 h-4 text-amber-600" />
            ) : (
              <ChevronRight className="w-4 h-4 text-amber-600" />
            )}
          </button>
          {highlightsOpen && (
            <div className="px-5 pb-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              {highlightGroups.map((group, gi) => (
                <div key={gi} className="bg-white/70 rounded-lg border border-amber-100 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      group.type === 'sprint' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {group.type === 'sprint' ? 'Sprint' : 'Kanban'}
                    </span>
                    <h4 className="font-semibold text-gray-900 text-sm">{group.team}</h4>
                  </div>
                  {group.subtitle && (
                    <p className="text-xs text-gray-400 mb-2">{group.subtitle}</p>
                  )}
                  <ul className="space-y-1.5">
                    {group.bullets.map((item, bi) => (
                      <li key={bi} className="flex items-start gap-2">
                        <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${HEALTH_DOT[item.color]}`} />
                        <span className="text-sm text-gray-700 leading-relaxed">{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── KPI Summary Cards ─── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard
          title="Sprint Teams"
          value={summary?.totalSprintTeams || 0}
          subtitle="Scrum-based"
          icon={Users}
          gradient="bg-gradient-to-br from-amber-500 to-amber-600"
        />
        <StatCard
          title="Kanban Teams"
          value={summary?.totalKanbanTeams || 0}
          subtitle="Flow-based"
          icon={Zap}
          gradient="bg-gradient-to-br from-purple-500 to-purple-600"
        />
        <StatCard
          title="Combined Velocity"
          value={`${summary?.combinedVelocity || 0} pts`}
          subtitle="Avg story points"
          icon={TrendingUp}
          gradient="bg-gradient-to-br from-amber-500 to-amber-600"
        />
        <StatCard
          title="Completion Rate"
          value={`${overallCompletion}%`}
          subtitle="Velocity / planned"
          icon={Target}
          gradient="bg-gradient-to-br from-emerald-500 to-emerald-600"
        />
        <StatCard
          title="Total Open Bugs"
          value={summary?.totalOpenBugs || 0}
          subtitle="Across all teams"
          icon={Bug}
          gradient="bg-gradient-to-br from-red-500 to-red-600"
        />
        <StatCard
          title="Avg Turnaround"
          value={`${summary?.avgTurnaroundDays || 0}d`}
          subtitle="Kanban teams"
          icon={Clock}
          gradient="bg-gradient-to-br from-orange-500 to-orange-600"
        />
      </div>

      {/* ─── Charts Row 1: Velocity & Completion ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-amber-500" />
            Sprint Velocity vs Spillover
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={velocityData} layout="vertical" margin={{ right: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="team" width={120} tick={{ fontSize: 12, fill: '#374151' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="velocity" name="Velocity" fill="#f59e0b" radius={[0, 4, 4, 0]} label={renderBarLabel} />
                <Bar dataKey="spillover" name="Spillover" fill="#ef4444" radius={[0, 4, 4, 0]} label={renderBarLabel} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-emerald-500" />
            Sprint Completion Rate by Team
          </h3>
          <div className="h-64 flex items-center justify-center">
            {completionData.length > 0 ? (
              <div className="flex items-center gap-6 w-full">
                <div className="flex-1 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={completionData} margin={{ top: 10, right: 30, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#374151' }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}%`} />
                      <Tooltip formatter={(v) => `${v}%`} />
                      <Bar dataKey="value" name="Completion %" radius={[4, 4, 0, 0]}>
                        {completionData.map((entry, idx) => (
                          <Cell key={idx} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-col gap-1.5 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm bg-green-500" />
                    <span className="text-gray-600">&ge; 80% Healthy</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm bg-amber-500" />
                    <span className="text-gray-600">60-79% Attention</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm bg-red-500" />
                    <span className="text-gray-600">&lt; 60% At Risk</span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-gray-400">No completion data available</p>
            )}
          </div>
        </div>
      </div>

      {/* ─── Charts Row 2: Throughput & Bugs ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-purple-500" />
            Kanban Throughput (Last 30 Days)
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={throughputData} margin={{ right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="team" tick={{ fontSize: 11, fill: '#374151' }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="created" name="Created" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="resolved" name="Resolved" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Bug className="w-5 h-5 text-red-500" />
            Open Bugs by Team
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bugData} layout="vertical" margin={{ right: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="team" width={140} tick={{ fontSize: 12, fill: '#374151' }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="bugs" name="Open Bugs" radius={[0, 4, 4, 0]} label={renderBarLabel}>
                  {bugData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.bugs > 10 ? '#ef4444' : entry.bugs > 5 ? '#f59e0b' : '#10b981'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* ─── Team Health Scorecard ─── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-500" />
            Team Health Scorecard
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Team</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Type</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Key Metric</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Secondary</th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Health</th>
                <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {scorecardRows.map((row) => (
                <tr
                  key={row.teamKey}
                  className="hover:bg-amber-50/50 cursor-pointer transition-colors"
                  onClick={() => onNavigate(row.sectionKey)}
                >
                  <td className="px-5 py-3.5 text-sm font-medium text-gray-900">{row.team}</td>
                  <td className="px-5 py-3.5">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      row.type === 'Sprint' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {row.type}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-gray-700">{row.metric}</td>
                  <td className="px-5 py-3.5 text-sm text-gray-500">{row.secondary}</td>
                  <td className="px-5 py-3.5 text-center">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${HEALTH_BADGE[row.health.color]}`}>
                      <span className={`w-2 h-2 rounded-full ${HEALTH_DOT[row.health.color]}`} />
                      {row.health.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <ArrowRight className="w-4 h-4 text-gray-300 inline" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ─── Active Sprint Progress ─── */}
      {sprintTeams?.some((t) => !t.error && t.activeSprint) && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-amber-500" />
            Active Sprint Progress
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sprintTeams
              .filter((t) => !t.error && t.activeSprint)
              .map((t) => (
                <div key={t.teamKey} className="border border-gray-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900 text-sm">{t.team}</h4>
                    <span className="text-sm font-bold text-amber-600">{t.activeSprint.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-3 mb-2">
                    <div
                      className={`h-3 rounded-full transition-all ${
                        t.activeSprint.progress >= 75
                          ? 'bg-green-500'
                          : t.activeSprint.progress >= 50
                            ? 'bg-amber-500'
                            : 'bg-red-500'
                      }`}
                      style={{ width: `${t.activeSprint.progress}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{t.activeSprint.name}</span>
                    <span>
                      {t.activeSprint.completedPoints}/{t.activeSprint.totalPoints} pts
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* ─── Sprint Teams ─── */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-amber-500" />
          Sprint Teams
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {sprintTeams?.map((team) => (
            <TeamCard
              key={team.teamKey}
              team={team}
              type="sprint"
              sectionKey={`technology-sprint-${team.teamKey}`}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </div>

      {/* ─── Kanban Teams ─── */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-purple-500" />
          Kanban Teams
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {kanbanTeams?.map((team) => (
            <TeamCard
              key={team.teamKey}
              team={team}
              type="kanban"
              sectionKey={`technology-kanban-${team.teamKey}`}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
